import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, Building2, MessageSquare, Star, Loader2, 
  Plus, Edit, Trash2, Check, X, Eye, EyeOff, FileText, Settings, Image, Store, Clock, Lock, RefreshCw, Download,
  ChevronUp, ChevronDown, GripVertical, Layers
} from 'lucide-react';
import { format } from 'date-fns';
import { ImageUploader, MultiImageUploader } from '@/components/ImageUploader';
import axios from 'axios';
import { toast } from 'sonner';
import { vendorCategoryNames, vendorCategories } from '@/lib/vendorCategories';
import {
  fetchVendorSelectableCategories,
  mergeWithSelectedCategories,
} from '@/lib/vendorCategorySource';
import { downloadVendorInvoicePdf } from '@/lib/vendorInvoicePdf';
import { getSeoPreviewMode, isPreviewEnvironment, setSeoPreviewMode } from '@/lib/seoPreviewMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildVendorPayloadFromForm = (formState) => {
  const primaryCategory = formState.primaryCategory || '';
  const normalizedSecondary = (formState.secondaryCategories || []).filter(
    (category) => category && category !== primaryCategory,
  );
  const isMrPlan = String(formState.pricing_plan || '').startsWith('MR ');

  return {
    ...formState,
    temp_password: undefined,
    primaryCategory,
    secondaryCategories: normalizedSecondary,
    category: primaryCategory,
    categories: primaryCategory ? [primaryCategory, ...normalizedSecondary] : [],
    admin_package_amount: isMrPlan
      ? (formState.admin_package_amount === '' ? null : Number(formState.admin_package_amount))
      : null,
  };
};

const formatPaymentDate = (dateValue) => {
  if (!dateValue) return 'Not set';
  const parsedDate = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return 'Not set';
  return format(parsedDate, 'dd/MM/yyyy');
};

const getNextPaymentDate = (pricingPlan, createdAt) => {
  if (!pricingPlan || !createdAt) return 'Not set';
  const startDate = new Date(createdAt);
  if (Number.isNaN(startDate.getTime())) return 'Not set';
  const nextDate = new Date(startDate);
  if (/annual/i.test(pricingPlan)) {
    nextDate.setFullYear(nextDate.getFullYear() + 1);
  } else {
    nextDate.setMonth(nextDate.getMonth() + 1);
  }
  return format(nextDate, 'dd/MM/yyyy');
};

const getVendorPaymentVisualState = (vendorAccount) => {
  if (!vendorAccount) return 'unpaid';
  const status = String(vendorAccount.payment_status || 'pending').toLowerCase();
  const hasPaymentMethod = Boolean(vendorAccount.payment_method);
  if (status === 'received') return 'paid';
  if (!hasPaymentMethod) return 'unpaid';
  return 'unpaid';
};

const BLOG_CATEGORIES = [
  'Wedding Tips',
  'Vendor Spotlight',
  'Real Weddings',
  'Planning Guide',
  'Style & Trends',
  'Gibraltar Guide'
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { token, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorApplications, setVendorApplications] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendorMessageStats, setVendorMessageStats] = useState([]);
  const [pageViewStats, setPageViewStats] = useState(null);
  const [vendorThreads, setVendorThreads] = useState([]);
  const [activeVendorThreadId, setActiveVendorThreadId] = useState(null);
  const [adminReplyDrafts, setAdminReplyDrafts] = useState({});
  const [uploadingFreeGuidePdf, setUploadingFreeGuidePdf] = useState(false);
  
  // Vendor Dialog
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorMessageDialogOpen, setVendorMessageDialogOpen] = useState(false);
  const [vendorMessageTarget, setVendorMessageTarget] = useState(null);
  const [vendorMessageForm, setVendorMessageForm] = useState({ subject: '', message: '' });
  const [generatingInvoiceFor, setGeneratingInvoiceFor] = useState(null);
  const [refreshingInvoiceDetailsFor, setRefreshingInvoiceDetailsFor] = useState(null);
  const [resettingVendorPasswordFor, setResettingVendorPasswordFor] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [loadingInvoicePreview, setLoadingInvoicePreview] = useState(false);
  const [vendorAutoSaveState, setVendorAutoSaveState] = useState('idle');
  const [vendorAutoSaveError, setVendorAutoSaveError] = useState('');
  const isHydratingVendorFormRef = useRef(false);
  const lastVendorAutoSavedSignatureRef = useRef('');
  const [vendorForm, setVendorForm] = useState({
    name: '',
    category: '',
    categories: [],
    primaryCategory: '',
    secondaryCategories: [],
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    price_range: '££',
    pricing_plan: 'Standard Monthly',
    admin_package_amount: '',
    next_payment_date: '',
    payment_method: 'Bank Transfer',
    invoice_name: '',
    payment_status: 'pending',
    payment_received_at: '',
    temp_password: '',
    images: [],
    logo: '',
    is_featured: false,
    is_approved: true
  });
  const [vendorSelectableCategories, setVendorSelectableCategories] = useState(vendorCategoryNames);

  const selectedVendorFormCategories = [
    vendorForm.primaryCategory,
    ...(vendorForm.secondaryCategories || []),
  ].filter(Boolean);
  const isMrPlanSelected = String(vendorForm.pricing_plan || '').startsWith('MR ');

  const vendorCategoryOptions = mergeWithSelectedCategories(
    vendorSelectableCategories,
    selectedVendorFormCategories,
  );

  // Blog Dialog
  const [editingPost, setEditingPost] = useState(null);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    featured_media_url: '',
    featured_media_type: 'image',
    gallery_images: [],
    video_url: '',
    linked_vendor_ids: [],
    category: 'Wedding Tips',
    is_published: false
  });
  const [selectedBlogVendorId, setSelectedBlogVendorId] = useState('');

  // Content Dialog
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  
  // Inquiry Dialog
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);

  const vendorMessageStatsMap = Object.fromEntries(vendorMessageStats.map((row) => [row._id, row]));
  const vendorPageViewMap = Object.fromEntries((pageViewStats?.by_vendor || []).map((row) => [row._id, row]));
  const vendorThreadUnreadCount = vendorThreads.filter((thread) => thread.status === 'vendor_replied').length;
  const vendorAccountMap = Object.fromEntries(vendorApplications.map((application) => [application.id, application]));

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/client/auth');
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      loadVendorSelectableCategories();
    }
  }, [isAdmin]);

  const loadVendorSelectableCategories = async () => {
    const categories = await fetchVendorSelectableCategories();
    setVendorSelectableCategories(categories);
  };

  const fetchData = async () => {
    try {
      const [statsRes, vendorsRes, applicationsRes, inquiriesRes, blogRes, contentRes, messageStatsRes, pageViewStatsRes, vendorThreadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vendor-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inquiries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/blog?published_only=false`),
        axios.get(`${API}/cms/content`),
        axios.get(`${API}/admin/vendor-message-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/page-view-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vendor-messages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats({ ...(statsRes.data || {}) });
      setVendors(Array.isArray(vendorsRes.data) ? [...vendorsRes.data] : []);
      setVendorApplications(Array.isArray(applicationsRes.data) ? [...applicationsRes.data] : []);
      setInquiries(Array.isArray(inquiriesRes.data) ? [...inquiriesRes.data] : []);
      setBlogPosts(Array.isArray(blogRes.data) ? [...blogRes.data] : []);
      setSiteContent(contentRes.data);
      setVendorMessageStats(Array.isArray(messageStatsRes.data) ? [...messageStatsRes.data] : []);
      setPageViewStats({ ...(pageViewStatsRes.data || {}) });
      setVendorThreads(Array.isArray(vendorThreadsRes.data) ? [...vendorThreadsRes.data] : []);
      if (Array.isArray(vendorThreadsRes.data) && vendorThreadsRes.data.length > 0) {
        setActiveVendorThreadId((current) => current || vendorThreadsRes.data[0]?.id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const topVendorMessages = useMemo(() => vendorMessageStats.slice(0, 6), [vendorMessageStats]);
  const topViewedVendors = useMemo(() => (Array.isArray(pageViewStats?.by_vendor) ? [...pageViewStats.by_vendor] : []), [pageViewStats]);
  const topPages = useMemo(() => (Array.isArray(pageViewStats?.by_page) ? [...pageViewStats.by_page] : []), [pageViewStats]);
  const recentTraffic = useMemo(() => (Array.isArray(pageViewStats?.daily) ? [...pageViewStats.daily] : []), [pageViewStats]);

  const getReadablePageName = (pagePath) => {
    const normalized = (pagePath || '/').split('?')[0].replace(/\/$/, '') || '/';
    const staticPages = {
      '/': 'Homepage',
      '/vendors': 'All Vendors',
      '/auth': 'Sign In',
      '/client/auth': 'Couples & Guests Portal',
      '/vendor/auth': 'Vendor Portal',
      '/planner': 'Wedding Planner',
      '/dashboard': 'My Favourites',
      '/admin': 'Admin Dashboard',
      '/blog': 'Blog',
      '/getting-married-in-gibraltar': 'Getting Married in Gibraltar',
      '/about-gibraltar': 'About Gibraltar',
      '/about-expo': 'About the Expo',
    };

    if (staticPages[normalized]) return staticPages[normalized];

    if (normalized.startsWith('/vendors/')) {
      const vendorReference = normalized.replace('/vendors/', '');
      const matchedVendor = vendors.find((vendor) => vendor.id === vendorReference || vendor.slug === vendorReference);
      return matchedVendor ? `Vendor Profile: ${matchedVendor.name}` : 'Vendor Profile';
    }

    return normalized.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) || 'Homepage';
  };

  // Vendor handlers
  const handleVendorFormChange = (name, value) => {
    setVendorForm(prev => ({ ...prev, [name]: value }));
    if (editingVendor && name !== 'temp_password') {
      setVendorAutoSaveState('idle');
      setVendorAutoSaveError('');
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: '',
      category: '',
      categories: [],
      primaryCategory: '',
      secondaryCategories: [],
      description: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      price_range: '$$',
      pricing_plan: 'Standard Monthly',
      admin_package_amount: '',
      next_payment_date: '',
      payment_method: 'Bank Transfer',
      invoice_name: '',
      payment_status: 'pending',
      payment_received_at: '',
      temp_password: '',
      images: [],
      logo: '',
      is_featured: false,
      is_approved: true
    });
    setVendorAutoSaveState('idle');
    setVendorAutoSaveError('');
    isHydratingVendorFormRef.current = false;
    lastVendorAutoSavedSignatureRef.current = '';
    setEditingVendor(null);
  };

  const openEditVendorDialog = (vendor) => {
    const resolvedPrimary = vendor.primaryCategory || vendor.category || (vendor.categories || [])[0] || '';
    const resolvedSecondary = (vendor.secondaryCategories || (vendor.categories || []).filter((category) => category !== resolvedPrimary)).filter(Boolean);

    const initialVendorForm = {
      name: vendor.name,
      category: resolvedPrimary,
      categories: resolvedPrimary ? [resolvedPrimary, ...resolvedSecondary] : resolvedSecondary,
      primaryCategory: resolvedPrimary,
      secondaryCategories: resolvedSecondary,
      description: vendor.description,
      address: vendor.address,
      phone: vendor.phone,
      email: vendor.email,
      website: vendor.website || '',
      price_range: vendor.price_range,
      pricing_plan: vendorAccountMap[vendor.vendor_account_id]?.pricing_plan || 'Standard Monthly',
      admin_package_amount: vendorAccountMap[vendor.vendor_account_id]?.admin_package_amount != null
        ? String(vendorAccountMap[vendor.vendor_account_id].admin_package_amount)
        : '',
      next_payment_date: vendorAccountMap[vendor.vendor_account_id]?.next_payment_date || '',
      payment_method: vendorAccountMap[vendor.vendor_account_id]?.payment_method || 'Bank Transfer',
      invoice_name: vendorAccountMap[vendor.vendor_account_id]?.invoice_name || '',
      payment_status: vendorAccountMap[vendor.vendor_account_id]?.payment_status || 'pending',
      payment_received_at: vendorAccountMap[vendor.vendor_account_id]?.payment_received_at || '',
      temp_password: '',
      images: vendor.images || [],
      logo: vendor.logo || '',
      is_featured: vendor.is_featured,
      is_approved: vendor.is_approved
    };

    isHydratingVendorFormRef.current = true;
    setEditingVendor(vendor);
    setVendorForm(initialVendorForm);
    setVendorAutoSaveState('idle');
    setVendorAutoSaveError('');
    lastVendorAutoSavedSignatureRef.current = JSON.stringify(buildVendorPayloadFromForm(initialVendorForm));

    setVendorDialogOpen(true);
    loadLatestInvoicePreview(vendor.vendor_account_id);

    window.setTimeout(() => {
      isHydratingVendorFormRef.current = false;
    }, 0);
  };

  const loadLatestInvoicePreview = async (vendorAccountId) => {
    if (!vendorAccountId) {
      setInvoicePreview(null);
      return;
    }

    setLoadingInvoicePreview(true);
    try {
      const response = await axios.get(`${API}/admin/vendors/${vendorAccountId}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const invoices = Array.isArray(response.data) ? response.data : [];
      setInvoicePreview(invoices[0] || null);
    } catch (error) {
      setInvoicePreview(null);
    } finally {
      setLoadingInvoicePreview(false);
    }
  };

  const handleSaveVendor = async ({ silent = false, closeDialog = true, refreshData = true, payloadOverride = null } = {}) => {
    try {
      const payload = payloadOverride || buildVendorPayloadFromForm(vendorForm);
      if (!payload.primaryCategory) {
        if (silent) {
          setVendorAutoSaveState('error');
          setVendorAutoSaveError('Primary category is required');
          return false;
        }
        toast.error('Primary category is required');
        return;
      }

      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (silent) {
          setVendorAutoSaveState('saved');
          setVendorAutoSaveError('');
        } else {
          toast.success('Vendor updated successfully');
          await loadLatestInvoicePreview(editingVendor.vendor_account_id);
        }
      } else {
        await axios.post(`${API}/vendors`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vendor created successfully');
      }

      if (closeDialog) {
        setVendorDialogOpen(false);
        resetVendorForm();
      }
      if (refreshData) {
        fetchData();
      }
      return true;
    } catch (error) {
      if (silent) {
        setVendorAutoSaveState('error');
        setVendorAutoSaveError(error.response?.data?.detail || 'Autosave failed');
        return false;
      }
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
      return false;
    }
  };

  useEffect(() => {
    if (!vendorDialogOpen || !editingVendor) return;
    if (isHydratingVendorFormRef.current) return;

    const payload = buildVendorPayloadFromForm(vendorForm);
    if (!payload.primaryCategory) return;

    const signature = JSON.stringify(payload);
    if (signature === lastVendorAutoSavedSignatureRef.current) return;

    const timer = window.setTimeout(async () => {
      setVendorAutoSaveState('saving');
      setVendorAutoSaveError('');
      const saved = await handleSaveVendor({
        silent: true,
        closeDialog: false,
        refreshData: false,
        payloadOverride: payload,
      });

      if (saved) {
        lastVendorAutoSavedSignatureRef.current = signature;
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [vendorDialogOpen, editingVendor, vendorForm]);

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await axios.delete(`${API}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const openVendorMessageDialog = (vendor) => {
    setVendorMessageTarget(vendor);
    setVendorMessageForm({
      subject: `Hello ${vendor.name}`,
      message: '',
    });
    setVendorMessageDialogOpen(true);
  };

  const handleSendVendorMessage = async () => {
    if (!vendorMessageTarget || !vendorMessageForm.subject.trim() || !vendorMessageForm.message.trim()) return;
    try {
      await axios.post(`${API}/admin/vendors/${vendorMessageTarget.id}/message`, vendorMessageForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message sent to vendor.');
      setVendorMessageDialogOpen(false);
      setVendorMessageTarget(null);
      setVendorMessageForm({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send vendor message');
    }
  };

  const handleGenerateInvoice = async (vendorAccountId) => {
    if (!vendorAccountId) {
      toast.error('This vendor does not have an invoice account yet');
      return;
    }

    setGeneratingInvoiceFor(vendorAccountId);
    try {
      const response = await axios.post(`${API}/admin/vendors/${vendorAccountId}/invoices`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      downloadVendorInvoicePdf(response.data);
      toast.success(`Invoice ${response.data.invoice_number} downloaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoiceFor(null);
    }
  };

  const handleRefreshExistingInvoices = async () => {
    if (!editingVendor?.vendor_account_id) {
      toast.error('This vendor does not have an invoice account yet');
      return;
    }

    setRefreshingInvoiceDetailsFor(editingVendor.vendor_account_id);
    try {
      const response = await axios.post(
        `${API}/admin/vendors/${editingVendor.vendor_account_id}/invoices/refresh-payment-details`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${response.data?.refreshed_count || 0} existing invoice(s) with latest payment details.`);
      await loadLatestInvoicePreview(editingVendor.vendor_account_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh existing invoices');
    } finally {
      setRefreshingInvoiceDetailsFor(null);
    }
  };

  const handleResetVendorPassword = async () => {
    if (!editingVendor?.vendor_account_id) {
      toast.error('Vendor account not found for password reset');
      return;
    }

    const temporaryPassword = (vendorForm.temp_password || '').trim();
    if (temporaryPassword.length < 6) {
      toast.error('Temporary password must be at least 6 characters');
      return;
    }

    setResettingVendorPasswordFor(editingVendor.vendor_account_id);
    try {
      await axios.post(
        `${API}/admin/vendors/${editingVendor.vendor_account_id}/reset-password`,
        {
          temporary_password: temporaryPassword,
          force_change_on_login: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Temporary password set for ${editingVendor.name}`);
      handleVendorFormChange('temp_password', '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset vendor password');
    } finally {
      setResettingVendorPasswordFor(null);
    }
  };

  const handleAdminReplyToThread = async (threadId) => {
    const message = adminReplyDrafts[threadId]?.trim();
    if (!message) return;
    try {
      await axios.post(`${API}/admin/vendor-messages/${threadId}/reply`, { message }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reply sent to vendor.');
      setAdminReplyDrafts((prev) => ({ ...prev, [threadId]: '' }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleDeleteVendorThread = async (threadId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    try {
      await axios.delete(`${API}/admin/vendor-messages/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Conversation deleted');
      setActiveVendorThreadId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete conversation');
    }
  };

  const handleToggleApproval = async (vendor) => {
    try {
      await axios.put(`${API}/vendors/${vendor.id}`, 
        { is_approved: !vendor.is_approved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(vendor.is_approved ? 'Vendor unapproved' : 'Vendor approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to update vendor');
    }
  };

  const handleToggleVisibility = async (vendor) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendor.id}/visibility`, {
        is_visible: !vendor.is_visible,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(vendor.is_visible ? 'Vendor hidden from the website' : 'Vendor is now live on the website');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vendor visibility');
    }
  };

  const toggleVendorFormCategory = (category) => {
    setVendorForm((prev) => {
      if (!prev.primaryCategory) {
        return {
          ...prev,
          primaryCategory: category,
          category,
          categories: [category, ...(prev.secondaryCategories || [])],
        };
      }

      if (prev.primaryCategory === category) {
        return prev;
      }

      const secondaryCategories = prev.secondaryCategories || [];
      const nextSecondary = secondaryCategories.includes(category)
        ? secondaryCategories.filter((item) => item !== category)
        : [...secondaryCategories, category];

      return {
        ...prev,
        secondaryCategories: nextSecondary,
        categories: [prev.primaryCategory, ...nextSecondary],
      };
    });
  };

  const setVendorFormPrimaryCategory = (category) => {
    setVendorForm((prev) => {
      const nextSecondary = (prev.secondaryCategories || []).filter((item) => item !== category);
      return {
        ...prev,
        primaryCategory: category,
        category,
        secondaryCategories: nextSecondary,
        categories: [category, ...nextSecondary],
      };
    });
  };

  // Blog handlers
  const handleBlogFormChange = (name, value) => {
    setBlogForm(prev => ({ ...prev, [name]: value }));
    if (name === 'title' && !editingPost) {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      setBlogForm(prev => ({ ...prev, slug }));
    }
  };

  const handleAddLinkedVendorToBlog = () => {
    if (!selectedBlogVendorId || blogForm.linked_vendor_ids.includes(selectedBlogVendorId)) return;
    handleBlogFormChange('linked_vendor_ids', [...blogForm.linked_vendor_ids, selectedBlogVendorId]);
    setSelectedBlogVendorId('');
  };

  const handleRemoveLinkedVendorFromBlog = (vendorId) => {
    handleBlogFormChange('linked_vendor_ids', blogForm.linked_vendor_ids.filter((id) => id !== vendorId));
  };

  const resetBlogForm = () => {
    setBlogForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featured_image: '',
      featured_media_url: '',
      featured_media_type: 'image',
      gallery_images: [],
      video_url: '',
      linked_vendor_ids: [],
      category: 'Wedding Tips',
      is_published: false
    });
    setEditingPost(null);
    setSelectedBlogVendorId('');
  };

  const openEditBlogDialog = (post) => {
    setEditingPost(post);
    const inferredMediaType = post.featured_media_type
      || (post.featured_media_url
        ? (String(post.featured_media_type || '').toLowerCase() === 'video' ? 'video' : 'image')
        : (post.video_url ? 'video' : 'image'));
    const coverMediaUrl = post.featured_media_url || (inferredMediaType === 'video' ? (post.video_url || '') : (post.featured_image || ''));
    setBlogForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      featured_image: post.featured_image || '',
      featured_media_url: coverMediaUrl,
      featured_media_type: inferredMediaType,
      gallery_images: post.gallery_images || [],
      video_url: post.video_url || '',
      linked_vendor_ids: post.linked_vendor_ids || [],
      category: post.category,
      is_published: post.is_published
    });
    setSelectedBlogVendorId('');
    setBlogDialogOpen(true);
  };

  const handleSaveBlog = async () => {
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, blogForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/blog`, blogForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created successfully');
      }
      setBlogDialogOpen(false);
      resetBlogForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleDeleteBlog = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  // Content handlers
  const handleContentChange = (name, value) => {
    setSiteContent(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveContent = async () => {
    if (siteContent?.free_guide_enabled && !siteContent?.free_guide_pdf_storage_path) {
      toast.error('Upload a free guide PDF before enabling the button.');
      return;
    }

    try {
      await axios.put(`${API}/cms/content`, siteContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content saved successfully');
      setContentDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save content');
    }
  };

  const handleFreeGuidePdfUpload = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const isPdfType = selectedFile.type === 'application/pdf' || selectedFile.name.toLowerCase().endsWith('.pdf');
    if (!isPdfType) {
      toast.error('Only PDF files are allowed.');
      event.target.value = '';
      return;
    }

    setUploadingFreeGuidePdf(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/cms/free-guide/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSiteContent((prev) => ({
        ...prev,
        free_guide_pdf_url: response.data?.url || '',
        free_guide_pdf_storage_path: response.data?.storage_path || '',
        free_guide_pdf_filename: response.data?.filename || selectedFile.name,
      }));

      toast.success('Free guide PDF uploaded successfully.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload free guide PDF.');
    } finally {
      setUploadingFreeGuidePdf(false);
      event.target.value = '';
    }
  };

  // Inquiry handlers
  const handleInquiryStatus = async (inquiryId, status) => {
    try {
      await axios.put(`${API}/inquiries/${inquiryId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Vendor Application handlers
  const handleApproveVendor = async (vendorId) => {
    if (!window.confirm('Approve this vendor application? This will create their public listing(s).')) return;
    try {
      await axios.put(`${API}/admin/vendor-applications/${vendorId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor approved successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve vendor');
    }
  };

  const handleRejectVendor = async (vendorId) => {
    const reason = window.prompt('Rejection reason (optional):');
    try {
      await axios.put(`${API}/admin/vendor-applications/${vendorId}/reject?reason=${encodeURIComponent(reason || '')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor application rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject vendor');
    }
  };

  const handleToggleVendorFeatured = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendor-applications/${vendorId}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Featured status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const handleDeleteVendorApplication = async (vendorId) => {
    if (!window.confirm('Delete this vendor and all their listings? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/vendor-applications/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen py-24" data-testid="admin-page">
      <div className="container-wedding">
        {/* Header */}
        <div className="mb-12">
          <p className="caption mb-4">Administration</p>
          <h1 className="heading-xl">Admin Dashboard</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4 mb-12">
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="h-5 w-5 text-[#5C0120]" />
              <span className="text-sm text-stone-500">Vendors</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_vendors || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Store className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-sm text-stone-500">Applications</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_vendor_accounts || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-sm text-stone-500">Pending</span>
            </div>
            <p className="text-3xl font-medium">{stats?.pending_applications || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-5 w-5 text-[#5C0120]" />
              <span className="text-sm text-stone-500">Users</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_users || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <Star className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-sm text-stone-500">Featured Req</span>
            </div>
            <p className="text-3xl font-medium">{stats?.featured_requests || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="h-5 w-5 text-[#5C0120]" />
              <span className="text-sm text-stone-500">Inquiries</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_inquiries || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-sm text-stone-500">Blog Posts</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_blog_posts || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow" data-testid="admin-total-messages-card">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="h-5 w-5 text-[#5C0120]" />
              <span className="text-sm text-stone-500">Messages</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_messages || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow" data-testid="admin-total-page-views-card">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-sm text-stone-500">Page Views</span>
            </div>
            <p className="text-3xl font-medium">{stats?.total_page_views || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="bg-white border border-stone-100 flex h-auto flex-wrap justify-start gap-2 p-2">
            <TabsTrigger value="analytics" data-testid="admin-analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="applications">
              Applications ({vendorApplications.filter(v => v.status === 'pending').length} pending)
            </TabsTrigger>
            <TabsTrigger value="vendors">Vendors ({vendors.length})</TabsTrigger>
            <TabsTrigger value="vendor-messages">
              Vendor Messages ({vendorThreads.length})
              {vendorThreadUnreadCount > 0 && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">{vendorThreadUnreadCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-users-tab">Users</TabsTrigger>
            <TabsTrigger value="blog">Blog ({blogPosts.length})</TabsTrigger>
            <TabsTrigger value="categories" data-testid="admin-categories-tab">Categories</TabsTrigger>
            <TabsTrigger value="content">Site Content</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6" data-testid="admin-traffic-overview-panel">
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div>
                      <h3 className="font-medium text-lg">Traffic Overview</h3>
                      <p className="text-sm text-stone-500">Vendor page views and overall directory activity.</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-[0.18em] text-stone-400">Today</p>
                      <p className="text-2xl font-semibold text-[#5C0120]" data-testid="admin-today-page-views">{stats?.today_page_views || 0}</p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-sm border border-stone-100 p-4">
                      <p className="text-sm text-stone-500 mb-3">Most Viewed Vendor Profiles</p>
                      <div className="space-y-3">
                        {topViewedVendors.length > 0 ? topViewedVendors.map((item) => (
                          <div key={item._id} className="flex items-center justify-between gap-3" data-testid={`admin-top-vendor-${item._id}`}>
                            <div>
                              <p className="font-medium text-stone-800">{item.vendor_name}</p>
                              <p className="text-xs text-stone-500">Vendor profile</p>
                            </div>
                            <span className="rounded-full bg-[#5C0120]/10 px-3 py-1 text-sm font-medium text-[#5C0120]">{item.views} views</span>
                          </div>
                        )) : <p className="text-sm text-stone-500">No vendor page views yet.</p>}
                      </div>
                    </div>

                    <div className="rounded-sm border border-stone-100 p-4">
                      <p className="text-sm text-stone-500 mb-3">Most Visited Pages</p>
                      <div className="space-y-3">
                        {topPages.length > 0 ? topPages.map((item, index) => (
                          <div key={`${item._id}-${index}`} className="space-y-1" data-testid={`admin-top-page-${index}`}>
                            <div className="flex items-center justify-between gap-3 text-sm">
                              <span className="font-medium text-stone-800 break-all">{item.page_label || getReadablePageName(item._id) || 'Homepage'}</span>
                              <span className="text-stone-500">{item.views}</span>
                            </div>
                            <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                              <div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${Math.max(12, (item.views / Math.max(topPages[0]?.views || 1, 1)) * 100)}%` }} />
                            </div>
                          </div>
                        )) : <p className="text-sm text-stone-500">No page traffic recorded yet.</p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6" data-testid="admin-message-analytics-panel">
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div>
                      <h3 className="font-medium text-lg">Vendor Inbox Analytics</h3>
                      <p className="text-sm text-stone-500">Conversation volume and reply status by vendor.</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-[0.18em] text-stone-400">Unread</p>
                      <p className="text-2xl font-semibold text-[#5C0120]">{stats?.unread_messages || 0}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {topVendorMessages.length > 0 ? topVendorMessages.map((item) => (
                      <div key={item._id} className="rounded-sm border border-stone-100 p-4" data-testid={`admin-vendor-message-row-${item._id}`}>
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="font-medium text-stone-800">{item.vendor_name}</p>
                            <p className="text-sm text-stone-500">{item.conversation_count} conversation{item.conversation_count !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="rounded-full bg-[#5C0120]/10 px-3 py-1 text-[#5C0120]">{item.total_messages} total messages</span>
                            <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">{item.unread_conversations} unread</span>
                            <span className="rounded-full bg-green-100 px-3 py-1 text-green-700">{item.replied_conversations} replied</span>
                          </div>
                        </div>
                      </div>
                    )) : <p className="text-sm text-stone-500">No vendor inbox activity recorded yet.</p>}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6" data-testid="admin-daily-traffic-panel">
                <h3 className="font-medium text-lg mb-2">Last 7 Days</h3>
                <p className="text-sm text-stone-500 mb-6">Daily page view trend.</p>

                <div className="space-y-4">
                  {recentTraffic.length > 0 ? recentTraffic.map((day) => (
                    <div key={day._id} className="space-y-2" data-testid={`admin-daily-traffic-${day._id}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-stone-700">{day._id}</span>
                        <span className="text-stone-500">{day.views} views</span>
                      </div>
                      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full rounded-full bg-[#5C0120]" style={{ width: `${Math.max(12, (day.views / Math.max(recentTraffic.reduce((max, current) => Math.max(max, current.views), 1), 1)) * 100)}%` }} />
                      </div>
                    </div>
                  )) : <p className="text-sm text-stone-500">No traffic data recorded in the last 7 days.</p>}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Vendor Applications Tab */}
          <TabsContent value="applications">
            <div className="bg-white rounded-sm border border-stone-100 card-shadow">
              <div className="p-4 border-b border-stone-100">
                <h3 className="font-medium">Vendor Applications</h3>
                <p className="text-sm text-stone-500">Review and approve vendor registrations</p>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Categories</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorApplications.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{vendor.business_name}</p>
                          <p className="text-xs text-stone-500 max-w-[280px] whitespace-pre-wrap break-words">{vendor.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                          {(vendor.primaryCategory || vendor.category) && (
                            <span className="rounded-full bg-[#5C0120]/10 px-2 py-0.5 text-xs font-medium text-[#5C0120]" data-testid={`admin-application-primary-category-${vendor.id}`}>
                              Primary: {vendor.primaryCategory || vendor.category}
                            </span>
                          )}
                          {(vendor.secondaryCategories || (vendor.categories || []).filter((cat) => cat !== (vendor.primaryCategory || vendor.category))).slice(0, 2).map((cat, i) => (
                            <span key={i} className="bg-stone-100 text-stone-600 px-2 py-0.5 rounded text-xs" data-testid={`admin-application-secondary-category-${vendor.id}-${i}`}>
                              {cat}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{vendor.email}</p>
                          <p className="text-stone-500">{vendor.phone}</p>
                          <p className="text-stone-500">Payment: {vendor.payment_method || 'Not set'}</p>
                          <p className="text-stone-500">Invoice to: {vendor.invoice_name || vendor.business_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          vendor.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : vendor.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {vendor.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {vendor.status === 'approved' && (
                          <button
                            onClick={() => handleToggleVendorFeatured(vendor.id)}
                            className={`p-1 rounded ${vendor.is_featured ? 'text-[#D4AF37]' : 'text-stone-300'}`}
                            title={vendor.featured_requested ? 'Featured Requested!' : 'Toggle Featured'}
                          >
                            <Star className={`h-5 w-5 ${vendor.is_featured ? 'fill-current' : ''} ${vendor.featured_requested && !vendor.is_featured ? 'animate-pulse' : ''}`} />
                          </button>
                        )}
                        {vendor.featured_requested && !vendor.is_featured && (
                          <span className="text-xs text-amber-600 block">Requested</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {vendor.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleApproveVendor(vendor.id)}
                                title="Approve"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleRejectVendor(vendor.id)}
                                title="Reject"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleGenerateInvoice(vendor.id)}
                            title="Generate Invoice"
                            className="text-[#5C0120] hover:bg-[#5C0120]/10"
                            disabled={generatingInvoiceFor === vendor.id}
                            data-testid={`admin-generate-application-invoice-${vendor.id}`}
                          >
                            {generatingInvoiceFor === vendor.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDeleteVendorApplication(vendor.id)}
                            title="Delete"
                            className="text-stone-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {vendorApplications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                        No vendor applications yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <div className="bg-white rounded-sm border border-stone-100 card-shadow">
              <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                <h3 className="font-medium">Manage Vendors</h3>
                <Dialog open={vendorDialogOpen} onOpenChange={(open) => {
                  setVendorDialogOpen(open);
                  if (!open) {
                    resetVendorForm();
                    fetchData();
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-vendor-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Vendor
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
                      </DialogTitle>
                      {editingVendor && (
                        <DialogDescription data-testid="admin-vendor-autosave-status">
                          {vendorAutoSaveState === 'saving'
                            ? 'Autosaving changes…'
                            : vendorAutoSaveState === 'saved'
                              ? 'Changes saved automatically'
                              : vendorAutoSaveState === 'error'
                                ? `Autosave failed: ${vendorAutoSaveError || 'Please try again.'}`
                                : 'Autosave is enabled'}
                        </DialogDescription>
                      )}
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input
                            value={vendorForm.name}
                            onChange={(e) => handleVendorFormChange('name', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="admin-vendor-primary-category">Primary Category *</Label>
                          <p className="text-xs text-stone-500">Primary category determines top ranking placement on that category page.</p>
                          <Select
                            value={vendorForm.primaryCategory || undefined}
                            onValueChange={setVendorFormPrimaryCategory}
                          >
                            <SelectTrigger id="admin-vendor-primary-category" className="input-wedding" data-testid="admin-vendor-primary-category-trigger">
                              <SelectValue placeholder="Select primary category" />
                            </SelectTrigger>
                            <SelectContent>
                              {vendorCategoryOptions.map((cat) => (
                                <SelectItem key={cat} value={cat} data-testid={`admin-vendor-primary-category-option-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                  {cat}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Secondary Categories (Optional)</Label>
                          <p className="text-xs text-stone-500">Secondary vendors appear after primary vendors on category pages.</p>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="admin-vendor-secondary-categories-grid">
                            {vendorCategoryOptions.map((cat) => {
                              const isPrimary = vendorForm.primaryCategory === cat;
                              const isSelected = (vendorForm.secondaryCategories || []).includes(cat);
                              return (
                                <button
                                  key={cat}
                                  type="button"
                                  onClick={() => toggleVendorFormCategory(cat)}
                                  disabled={isPrimary}
                                  className={`rounded-sm border px-3 py-2 text-left text-sm transition-colors ${isPrimary ? 'cursor-not-allowed border-stone-200 bg-stone-100 text-stone-400' : isSelected ? 'border-[#5C0120] bg-[#5C0120]/10 text-[#5C0120]' : 'border-stone-200 bg-white text-stone-600 hover:border-[#5C0120]/40'}`}
                                  data-testid={`admin-vendor-secondary-category-option-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                >
                                  {cat}{isPrimary ? ' (Primary)' : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={vendorForm.description}
                          onChange={(e) => handleVendorFormChange('description', e.target.value)}
                          rows={3}
                          className="input-wedding resize-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Address</Label>
                          <Input
                            value={vendorForm.address}
                            onChange={(e) => handleVendorFormChange('address', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={vendorForm.phone}
                            onChange={(e) => handleVendorFormChange('phone', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={vendorForm.email}
                            onChange={(e) => handleVendorFormChange('email', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Website</Label>
                          <Input
                            value={vendorForm.website}
                            onChange={(e) => handleVendorFormChange('website', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Price Range</Label>
                        <Select
                          value={vendorForm.price_range}
                          onValueChange={(value) => handleVendorFormChange('price_range', value)}
                        >
                          <SelectTrigger className="input-wedding w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="£">£</SelectItem>
                            <SelectItem value="££">££</SelectItem>
                            <SelectItem value="£££">£££</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Subscription</Label>
                        <Select
                          value={vendorForm.pricing_plan}
                          onValueChange={(value) => handleVendorFormChange('pricing_plan', value)}
                        >
                          <SelectTrigger className="input-wedding w-56" data-testid="admin-vendor-pricing-plan-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Standard Monthly">Standard Monthly</SelectItem>
                            <SelectItem value="Annual">Annual</SelectItem>
                            <SelectItem value="Featured Monthly">Featured Monthly</SelectItem>
                            <SelectItem value="Featured Annual">Featured Annual</SelectItem>
                            <SelectItem value="MR Monthly">MR Monthly</SelectItem>
                            <SelectItem value="MR Annual">MR Annual</SelectItem>
                            <SelectItem value="Charity">Charity</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {isMrPlanSelected && (
                        <div className="space-y-2" data-testid="admin-vendor-mr-amount-wrap">
                          <Label htmlFor="admin-vendor-mr-amount">MR Plan Amount (£)</Label>
                          <Input
                            id="admin-vendor-mr-amount"
                            type="number"
                            min="0"
                            step="0.01"
                            value={vendorForm.admin_package_amount}
                            onChange={(e) => handleVendorFormChange('admin_package_amount', e.target.value)}
                            className="input-wedding w-56"
                            placeholder="Optional custom amount"
                            data-testid="admin-vendor-mr-amount-input"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="admin-vendor-next-payment-date">Next Payment Date</Label>
                        <Input
                          id="admin-vendor-next-payment-date"
                          type="date"
                          value={vendorForm.next_payment_date || ''}
                          onChange={(e) => handleVendorFormChange('next_payment_date', e.target.value)}
                          className="input-wedding w-56"
                          data-testid="admin-vendor-next-payment-date-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Invoice Payment Method</Label>
                        <Select
                          value={vendorForm.payment_method || 'Bank Transfer'}
                          onValueChange={(value) => handleVendorFormChange('payment_method', value)}
                        >
                          <SelectTrigger className="input-wedding w-56" data-testid="admin-vendor-payment-method-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                            <SelectItem value="By Revolut">Revolut</SelectItem>
                            <SelectItem value="By Card">Card</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="admin-vendor-invoice-name-input">Invoice Made Out To</Label>
                        <Input
                          id="admin-vendor-invoice-name-input"
                          value={vendorForm.invoice_name || ''}
                          onChange={(e) => handleVendorFormChange('invoice_name', e.target.value)}
                          className="input-wedding w-80"
                          placeholder="Business or recipient name"
                          data-testid="admin-vendor-invoice-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Payment Status</Label>
                        <Select
                          value={vendorForm.payment_status || 'pending'}
                          onValueChange={(value) => handleVendorFormChange('payment_status', value)}
                        >
                          <SelectTrigger className="input-wedding w-56" data-testid="admin-vendor-payment-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="received">Received</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="admin-vendor-payment-received-at-input">Payment Received Date</Label>
                        <Input
                          id="admin-vendor-payment-received-at-input"
                          type="date"
                          value={vendorForm.payment_received_at || ''}
                          onChange={(e) => handleVendorFormChange('payment_received_at', e.target.value)}
                          className="input-wedding w-56"
                          data-testid="admin-vendor-payment-received-at-input"
                        />
                      </div>

                      {editingVendor && (
                        <div className="space-y-3 rounded-sm border border-stone-200 bg-stone-50 p-3" data-testid="admin-vendor-password-reset-panel">
                          <div className="space-y-1">
                            <Label htmlFor="admin-vendor-temp-password">Temporary Password</Label>
                            <p className="text-xs text-stone-500">Setting this will force vendor to change password at next login.</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Input
                              id="admin-vendor-temp-password"
                              type="password"
                              value={vendorForm.temp_password || ''}
                              onChange={(e) => handleVendorFormChange('temp_password', e.target.value)}
                              placeholder="Set temporary password"
                              className="input-wedding w-64"
                              data-testid="admin-vendor-temp-password-input"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleResetVendorPassword}
                              disabled={resettingVendorPasswordFor === editingVendor.vendor_account_id}
                              data-testid="admin-vendor-temp-password-submit"
                            >
                              {resettingVendorPasswordFor === editingVendor.vendor_account_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                              Set Temp Password
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleRefreshExistingInvoices}
                              disabled={refreshingInvoiceDetailsFor === editingVendor.vendor_account_id || !editingVendor.vendor_account_id}
                              data-testid="admin-vendor-refresh-existing-invoices-button"
                            >
                              {refreshingInvoiceDetailsFor === editingVendor.vendor_account_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                              Refresh Existing Invoices
                            </Button>
                          </div>
                        </div>
                      )}

                      {editingVendor && (
                        <div className="space-y-2 rounded-sm border border-stone-200 bg-white p-3" data-testid="admin-vendor-current-invoice-preview">
                          <div className="flex items-center justify-between gap-2">
                            <Label className="text-sm font-semibold">Current Invoice Preview</Label>
                            {loadingInvoicePreview && <Loader2 className="h-4 w-4 animate-spin text-stone-500" />}
                          </div>

                          {invoicePreview ? (
                            <div className="text-sm text-stone-700 space-y-1">
                              <p data-testid="admin-vendor-current-invoice-number"><strong>No:</strong> {invoicePreview.invoice_number}</p>
                              <p data-testid="admin-vendor-current-invoice-method"><strong>Method:</strong> {invoicePreview.payment_method || 'Not set'}</p>
                              <p data-testid="admin-vendor-current-invoice-amount"><strong>Amount:</strong> £{Number(invoicePreview.amount || 0).toFixed(2)}</p>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => downloadVendorInvoicePdf(invoicePreview)}
                                data-testid="admin-vendor-current-invoice-download-button"
                              >
                                <Download className="mr-2 h-4 w-4" /> Download Current Invoice PDF
                              </Button>
                            </div>
                          ) : (
                            <p className="text-xs text-stone-500" data-testid="admin-vendor-current-invoice-empty">No invoice generated yet for this vendor.</p>
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Vendor Logo</Label>
                        <ImageUploader
                          token={token}
                          folder="vendors"
                          currentImage={vendorForm.logo}
                          onUpload={(imageUrl) => handleVendorFormChange('logo', imageUrl || '')}
                          aspectRatio="aspect-square"
                          label="Upload Logo"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Images</Label>
                        <MultiImageUploader
                          token={token}
                          folder="vendors"
                          currentImages={vendorForm.images}
                          onUpload={(images) => handleVendorFormChange('images', images)}
                          maxImages={5}
                        />
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={vendorForm.is_featured}
                            onCheckedChange={(checked) => handleVendorFormChange('is_featured', checked)}
                          />
                          <Label>Featured</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={vendorForm.is_approved}
                            onCheckedChange={(checked) => handleVendorFormChange('is_approved', checked)}
                          />
                          <Label>Approved</Label>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        {!editingVendor && (
                          <Button onClick={() => handleSaveVendor()} className="btn-primary" data-testid="admin-vendor-create-button">
                            Create Vendor
                          </Button>
                        )}
                        {editingVendor && vendorAutoSaveState === 'error' && (
                          <Button
                            variant="outline"
                            onClick={() => handleSaveVendor({
                              silent: false,
                              closeDialog: false,
                              refreshData: false,
                            })}
                            data-testid="admin-vendor-autosave-retry-button"
                          >
                            Retry Save
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => {
                          setVendorDialogOpen(false);
                          resetVendorForm();
                          fetchData();
                        }} data-testid="admin-vendor-dialog-close-button">
                          {editingVendor ? 'Done' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Next Payment Due</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Messages</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((vendor) => {
                    const vendorAccount = vendorAccountMap[vendor.vendor_account_id] || null;
                    const pricingPlan = vendorAccount?.pricing_plan || 'Not set';
                    const paymentVisualState = getVendorPaymentVisualState(vendorAccount);
                    const joinedDate = vendorAccount?.created_at ? format(new Date(vendorAccount.created_at), 'MMM d, yyyy') : 'Not set';
                    const nextPaymentDate = vendorAccount?.next_payment_date
                      ? formatPaymentDate(vendorAccount.next_payment_date)
                      : (vendorAccount?.created_at ? getNextPaymentDate(vendorAccount?.pricing_plan, vendorAccount?.created_at) : 'Not set');

                    return (
                    <TableRow
                      key={vendor.id}
                      className={paymentVisualState === 'paid' ? 'bg-emerald-50/60' : 'bg-rose-50/60'}
                      data-testid={`admin-vendor-payment-row-${vendor.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{vendor.name}</p>
                          <p className="text-sm text-stone-500">{vendor.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[220px]" data-testid={`admin-vendor-categories-${vendor.id}`}>
                          {(vendor.primaryCategory || vendor.category) && (
                            <span className="rounded-full bg-[#5C0120]/10 px-2 py-1 text-[11px] font-medium text-[#5C0120]" data-testid={`admin-vendor-primary-category-${vendor.id}`}>
                              {(vendor.primaryCategory || vendor.category)} (Primary)
                            </span>
                          )}
                          {(vendor.secondaryCategories || (vendor.categories || []).filter((category) => category !== (vendor.primaryCategory || vendor.category))).map((category) => (
                            <span key={category} className="rounded-full bg-stone-100 px-2 py-1 text-[11px] font-medium text-stone-700" data-testid={`admin-vendor-secondary-category-${vendor.id}-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                              {category}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>{pricingPlan}</TableCell>
                      <TableCell>{joinedDate}</TableCell>
                      <TableCell>{nextPaymentDate}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />
                          <span>{vendor.average_rating}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{vendorMessageStatsMap[vendor.id]?.total_messages || 0}</p>
                          <p className="text-xs text-stone-500">{vendorMessageStatsMap[vendor.id]?.conversation_count || 0} conversations</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{vendorPageViewMap[vendor.id]?.views || 0}</p>
                          <p className="text-xs text-stone-500">profile views</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleApproval(vendor)}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            vendor.is_approved
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {vendor.is_approved ? 'Approved' : 'Pending'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleVisibility(vendor)}
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded text-xs font-medium transition-colors ${
                            vendor.is_visible
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                          }`}
                          data-testid={`admin-vendor-visibility-toggle-${vendor.id}`}
                          title={vendor.is_visible ? 'Hide this vendor from all public pages' : 'Show this vendor on all public pages'}
                        >
                          {vendor.is_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                          {vendor.is_visible ? 'Live' : 'Hidden'}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => window.open(`/vendors/${vendor.slug || vendor.id}`, '_blank')} data-testid={`admin-view-vendor-${vendor.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openVendorMessageDialog(vendor)} title="Message Vendor" data-testid={`admin-message-vendor-${vendor.id}`}>
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleGenerateInvoice(vendor.vendor_account_id)}
                            title="Generate Invoice"
                            className="text-[#5C0120] hover:text-[#5C0120]"
                            disabled={!vendor.vendor_account_id || generatingInvoiceFor === vendor.vendor_account_id}
                            data-testid={`admin-generate-vendor-invoice-${vendor.id}`}
                          >
                            {generatingInvoiceFor === vendor.vendor_account_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditVendorDialog(vendor)}
                            data-testid={`admin-edit-vendor-${vendor.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteVendor(vendor.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>

              <Dialog open={vendorMessageDialogOpen} onOpenChange={setVendorMessageDialogOpen}>
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>Message Vendor</DialogTitle>
                    <DialogDescription>
                      Send an admin message to {vendorMessageTarget?.name}. They will receive it in their vendor dashboard and by email.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Subject</Label>
                      <Input value={vendorMessageForm.subject} onChange={(e) => setVendorMessageForm((prev) => ({ ...prev, subject: e.target.value }))} data-testid="admin-vendor-message-subject" />
                    </div>
                    <div className="space-y-2">
                      <Label>Message</Label>
                      <Textarea value={vendorMessageForm.message} onChange={(e) => setVendorMessageForm((prev) => ({ ...prev, message: e.target.value }))} rows={6} data-testid="admin-vendor-message-body" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button onClick={handleSendVendorMessage} className="btn-primary" data-testid="admin-vendor-message-send-button">Send Message</Button>
                      <Button variant="outline" onClick={() => setVendorMessageDialogOpen(false)}>Cancel</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          <TabsContent value="vendor-messages">
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
              <div className="bg-white rounded-sm border border-stone-100 card-shadow overflow-hidden">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="font-medium text-lg">Vendor Inbox</h3>
                  <p className="text-sm text-stone-500">Replies from vendors appear here.</p>
                </div>
                <div className="divide-y divide-stone-100 max-h-[720px] overflow-y-auto">
                  {vendorThreads.length === 0 ? (
                    <div className="p-6 text-sm text-stone-500">No admin/vendor messages yet.</div>
                  ) : vendorThreads.map((thread) => (
                    <button
                      key={thread.id}
                      onClick={() => setActiveVendorThreadId(thread.id)}
                      className={`w-full text-left p-4 transition-colors ${activeVendorThreadId === thread.id ? 'bg-[#5C0120]/5' : 'hover:bg-stone-50'}`}
                      data-testid={`admin-vendor-thread-${thread.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-stone-900">{thread.vendor_name}</p>
                          <p className="text-xs uppercase tracking-[0.14em] text-stone-500 mt-1">{thread.subject}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {thread.status === 'vendor_replied' && <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800">New</span>}
                          <span className="text-xs text-stone-400">{thread.messages?.length || 0}</span>
                        </div>
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-2 mt-2">{thread.messages?.[thread.messages.length - 1]?.message}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-sm border border-stone-100 card-shadow min-h-[420px] flex flex-col">
                {(() => {
                  const activeThread = vendorThreads.find((thread) => thread.id === activeVendorThreadId);
                  if (!activeThread) {
                    return <div className="p-6 text-sm text-stone-500">Select a vendor thread to view and reply.</div>;
                  }
                  return (
                    <>
                      <div className="p-6 border-b border-stone-100 flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-lg">{activeThread.subject}</h3>
                          <p className="text-sm text-stone-500">Conversation with {activeThread.vendor_name}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteVendorThread(activeThread.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-sm transition-colors"
                          title="Delete conversation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                        {activeThread.messages?.map((message) => (
                          <div key={message.id} className={`max-w-[85%] rounded-sm border p-4 ${message.sender_role === 'admin' ? 'ml-auto bg-[#5C0120] text-white border-[#5C0120]' : 'bg-stone-50 border-stone-200 text-stone-800'}`}>
                            <p className={`text-xs uppercase tracking-[0.14em] mb-2 ${message.sender_role === 'admin' ? 'text-white/70' : 'text-stone-500'}`}>{message.sender_name}</p>
                            <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.message}</p>
                          </div>
                        ))}
                      </div>
                      <div className="p-6 border-t border-stone-100 space-y-3">
                        <Textarea
                          rows={4}
                          placeholder="Reply to this vendor..."
                          value={adminReplyDrafts[activeThread.id] || ''}
                          onChange={(e) => setAdminReplyDrafts((prev) => ({ ...prev, [activeThread.id]: e.target.value }))}
                          data-testid="admin-vendor-thread-reply-input"
                        />
                        <div className="flex justify-end">
                          <Button onClick={() => handleAdminReplyToThread(activeThread.id)} className="btn-primary" data-testid="admin-vendor-thread-reply-button">Send Reply</Button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <div className="bg-white rounded-sm border border-stone-100 card-shadow">
              <div className="p-4 border-b border-stone-100">
                <h3 className="font-medium">Customer Inquiries</h3>
                <p className="text-sm text-stone-500">Click on an inquiry to view the full message</p>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Wedding Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((inquiry) => (
                    <TableRow 
                      key={inquiry.id} 
                      className="cursor-pointer hover:bg-stone-50"
                      onClick={() => {
                        setSelectedInquiry(inquiry);
                        setInquiryDialogOpen(true);
                      }}
                    >
                      <TableCell className="text-sm text-stone-500">
                        {format(new Date(inquiry.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{inquiry.name}</p>
                          <p className="text-sm text-stone-500">{inquiry.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{inquiry.vendor_name}</TableCell>
                      <TableCell>{inquiry.wedding_date || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          inquiry.status === 'pending'
                            ? 'bg-amber-100 text-amber-700'
                            : inquiry.status === 'contacted'
                            ? 'bg-[#D4AF37]/15 text-[#5C0120]'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {inquiry.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => {
                              setSelectedInquiry(inquiry);
                              setInquiryDialogOpen(true);
                            }} 
                            title="View Message"
                          >
                            <Eye className="h-4 w-4 text-stone-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleInquiryStatus(inquiry.id, 'contacted')} title="Mark as Contacted">
                            <Check className="h-4 w-4 text-[#5C0120]" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleInquiryStatus(inquiry.id, 'completed')} title="Mark as Completed">
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Inquiry Detail Dialog */}
            <Dialog open={inquiryDialogOpen} onOpenChange={setInquiryDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Inquiry Details</DialogTitle>
                </DialogHeader>
                {selectedInquiry && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-stone-500">Customer Name</Label>
                        <p className="font-medium">{selectedInquiry.name}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">Email</Label>
                        <p className="font-medium">{selectedInquiry.email}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">Phone</Label>
                        <p className="font-medium">{selectedInquiry.phone || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">Wedding Date</Label>
                        <p className="font-medium">{selectedInquiry.wedding_date || 'Not specified'}</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Vendor</Label>
                      <p className="font-medium">{selectedInquiry.vendor_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Message</Label>
                      <div className="mt-1 p-3 bg-stone-50 rounded-sm">
                        <p className="whitespace-pre-wrap">{selectedInquiry.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div>
                        <Label className="text-xs text-stone-500">Status</Label>
                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                          selectedInquiry.status === 'pending'
                            ? 'bg-amber-100 text-amber-700'
                            : selectedInquiry.status === 'contacted'
                            ? 'bg-[#D4AF37]/15 text-[#5C0120]'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {selectedInquiry.status}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            handleInquiryStatus(selectedInquiry.id, 'contacted');
                            setSelectedInquiry({...selectedInquiry, status: 'contacted'});
                          }}
                        >
                          Mark Contacted
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            handleInquiryStatus(selectedInquiry.id, 'completed');
                            setSelectedInquiry({...selectedInquiry, status: 'completed'});
                          }}
                        >
                          Mark Completed
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <AdminUsersSection token={token} />
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="bg-white rounded-sm border border-stone-100 card-shadow">
              <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                <h3 className="font-medium">Manage Blog Posts</h3>
                <Dialog open={blogDialogOpen} onOpenChange={(open) => {
                  setBlogDialogOpen(open);
                  if (!open) resetBlogForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-blog-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Post
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingPost ? 'Edit Blog Post' : 'Create Blog Post'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={blogForm.title}
                            onChange={(e) => handleBlogFormChange('title', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Slug</Label>
                          <Input
                            value={blogForm.slug}
                            onChange={(e) => handleBlogFormChange('slug', e.target.value)}
                            className="input-wedding"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select
                          value={blogForm.category}
                          onValueChange={(value) => handleBlogFormChange('category', value)}
                        >
                          <SelectTrigger className="input-wedding w-[200px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BLOG_CATEGORIES.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label data-testid="blog-featured-media-label">Featured Media</Label>
                        <ImageUploader
                          token={token}
                          folder="blog"
                          currentImage={blogForm.featured_media_url || blogForm.featured_image || blogForm.video_url}
                          currentMediaType={blogForm.featured_media_type}
                          onUpload={(url, _storagePath, mediaType) => {
                            const resolvedType = mediaType || 'image';
                            handleBlogFormChange('featured_media_url', url || '');
                            handleBlogFormChange('featured_media_type', resolvedType);
                            if (resolvedType === 'video') {
                              handleBlogFormChange('video_url', url || '');
                              handleBlogFormChange('featured_image', '');
                            } else {
                              handleBlogFormChange('featured_image', url || '');
                              if ((blogForm.video_url || '') === (blogForm.featured_media_url || '')) {
                                handleBlogFormChange('video_url', '');
                              }
                            }
                          }}
                          aspectRatio="aspect-video"
                          uploadType="media"
                          label="Upload image or video (max 50MB)"
                        />
                        <p className="text-xs text-stone-500" data-testid="blog-featured-media-helptext">Supports JPG, JPEG, PNG, MP4, MOV, and WebM.</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Blog Photos</Label>
                        <MultiImageUploader
                          token={token}
                          folder="blog"
                          currentImages={blogForm.gallery_images || []}
                          onUpload={(images) => handleBlogFormChange('gallery_images', images)}
                          maxImages={8}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Blog Video</Label>
                        <ImageUploader
                          token={token}
                          folder="blog"
                          currentImage={blogForm.video_url}
                          onUpload={(url) => handleBlogFormChange('video_url', url)}
                          uploadType="video"
                          label="Upload Video"
                        />
                        <p className="text-xs text-stone-500">Optional: add one video. Leave blank if you want a text-only post.</p>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <Label>Linked Vendors</Label>
                            <p className="text-xs text-stone-500">The first linked vendor will be treated as the primary one on the blog post.</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select value={selectedBlogVendorId} onValueChange={setSelectedBlogVendorId}>
                              <SelectTrigger className="input-wedding w-64" data-testid="blog-linked-vendor-select">
                                <SelectValue placeholder="Select vendor" />
                              </SelectTrigger>
                              <SelectContent>
                                {vendors.map((vendor) => (
                                  <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button type="button" variant="outline" onClick={handleAddLinkedVendorToBlog} data-testid="blog-add-linked-vendor-button">
                              <Plus className="h-4 w-4 mr-2" />
                              Add
                            </Button>
                          </div>
                        </div>

                        {blogForm.linked_vendor_ids.length > 0 && (
                          <div className="flex flex-wrap gap-2" data-testid="blog-linked-vendors-list">
                            {blogForm.linked_vendor_ids.map((vendorId, index) => {
                              const vendor = vendors.find((item) => item.id === vendorId);
                              if (!vendor) return null;
                              return (
                                <div key={vendorId} className="flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700">
                                  <span>{index === 0 ? `Primary: ${vendor.name}` : vendor.name}</span>
                                  <button type="button" onClick={() => handleRemoveLinkedVendorFromBlog(vendorId)} className="text-stone-400 hover:text-red-600" data-testid={`blog-remove-linked-vendor-${vendorId}`}>
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Excerpt</Label>
                        <Textarea
                          value={blogForm.excerpt}
                          onChange={(e) => handleBlogFormChange('excerpt', e.target.value)}
                          rows={2}
                          placeholder="Brief summary for listing pages..."
                          className="input-wedding resize-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Content</Label>
                        <Textarea
                          value={blogForm.content}
                          onChange={(e) => handleBlogFormChange('content', e.target.value)}
                          rows={10}
                          placeholder="Full blog post content..."
                          className="input-wedding resize-none"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={blogForm.is_published}
                          onCheckedChange={(checked) => handleBlogFormChange('is_published', checked)}
                        />
                        <Label>Published</Label>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button onClick={handleSaveBlog} className="btn-primary">
                          {editingPost ? 'Update Post' : 'Create Post'}
                        </Button>
                        <Button variant="outline" onClick={() => {
                          setBlogDialogOpen(false);
                          resetBlogForm();
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blogPosts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell>
                        <p className="font-medium">{post.title}</p>
                        <p className="text-sm text-stone-500">/{post.slug}</p>
                      </TableCell>
                      <TableCell>{post.category}</TableCell>
                      <TableCell>{post.author_name}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          post.is_published ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
                        }`}>
                          {post.is_published ? 'Published' : 'Draft'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-stone-500">
                        {format(new Date(post.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEditBlogDialog(post)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteBlog(post.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-medium text-lg">Edit Site Content</h3>
                <Button onClick={handleSaveContent} className="btn-primary">
                  Save Changes
                </Button>
              </div>

              {siteContent && (
                <div className="space-y-8">
                  {/* Hero Section */}
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Image className="h-5 w-5 text-[#5C0120]" />
                      Hero Section
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Headline</Label>
                        <Input
                          value={siteContent.hero_headline || ''}
                          onChange={(e) => handleContentChange('hero_headline', e.target.value)}
                          className="input-wedding"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Subheadline</Label>
                        <Input
                          value={siteContent.hero_subheadline || ''}
                          onChange={(e) => handleContentChange('hero_subheadline', e.target.value)}
                          className="input-wedding"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={siteContent.hero_description || ''}
                        onChange={(e) => handleContentChange('hero_description', e.target.value)}
                        rows={2}
                        className="input-wedding resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hero Images (Slideshow)</Label>
                      <MultiImageUploader
                        token={token}
                        folder="hero"
                        currentImages={siteContent.hero_images || []}
                        onUpload={(images) => handleContentChange('hero_images', images)}
                        maxImages={5}
                      />
                    </div>
                  </div>

                  {/* About Section */}
                  <div className="space-y-4 pt-6 border-t border-stone-100">
                    <h4 className="font-medium flex items-center gap-2">
                      <FileText className="h-5 w-5 text-[#5C0120]" />
                      About Section
                    </h4>
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={siteContent.about_title || ''}
                        onChange={(e) => handleContentChange('about_title', e.target.value)}
                        className="input-wedding"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={siteContent.about_description || ''}
                        onChange={(e) => handleContentChange('about_description', e.target.value)}
                        rows={3}
                        className="input-wedding resize-none"
                      />
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4 pt-6 border-t border-stone-100">
                    <h4 className="font-medium flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-[#5C0120]" />
                      Contact Information
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          value={siteContent.contact_email || ''}
                          onChange={(e) => handleContentChange('contact_email', e.target.value)}
                          className="input-wedding"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={siteContent.contact_phone || ''}
                          onChange={(e) => handleContentChange('contact_phone', e.target.value)}
                          className="input-wedding"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Address</Label>
                        <Input
                          value={siteContent.contact_address || ''}
                          onChange={(e) => handleContentChange('contact_address', e.target.value)}
                          className="input-wedding"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Free Guide CTA Controls */}
                  <div className="space-y-4 pt-6 border-t border-stone-100" data-testid="admin-content-free-guide-section">
                    <h4 className="font-medium flex items-center gap-2">
                      <FileText className="h-5 w-5 text-[#5C0120]" />
                      Free Guide CTA
                    </h4>

                    <div className="rounded-sm border border-stone-200 bg-stone-50 p-4 space-y-4">
                      <div className="flex items-center justify-between gap-4" data-testid="admin-content-free-guide-toggle-row">
                        <div>
                          <p className="font-medium text-stone-900">Show Free Guide buttons on homepage</p>
                          <p className="text-xs text-stone-500">Can only be enabled after a PDF is uploaded.</p>
                        </div>
                        <Switch
                          checked={Boolean(siteContent.free_guide_enabled)}
                          onCheckedChange={(checked) => {
                            if (checked && !siteContent.free_guide_pdf_storage_path) {
                              toast.error('Upload a free guide PDF before enabling this.');
                              return;
                            }
                            handleContentChange('free_guide_enabled', checked);
                          }}
                          disabled={!siteContent.free_guide_pdf_storage_path && !siteContent.free_guide_enabled}
                          data-testid="admin-content-free-guide-enabled-switch"
                        />
                      </div>

                      <div className="space-y-2" data-testid="admin-content-free-guide-upload-wrap">
                        <Label htmlFor="admin-content-free-guide-pdf-input">Upload Free Guide PDF</Label>
                        <Input
                          id="admin-content-free-guide-pdf-input"
                          type="file"
                          accept="application/pdf"
                          onChange={handleFreeGuidePdfUpload}
                          disabled={uploadingFreeGuidePdf}
                          data-testid="admin-content-free-guide-pdf-input"
                        />
                        {uploadingFreeGuidePdf && (
                          <p className="text-xs text-stone-500" data-testid="admin-content-free-guide-uploading-text">Uploading PDF...</p>
                        )}
                        {siteContent.free_guide_pdf_filename && (
                          <div className="text-xs text-stone-600" data-testid="admin-content-free-guide-current-file">
                            Current PDF: <span className="font-medium">{siteContent.free_guide_pdf_filename}</span>
                            {siteContent.free_guide_pdf_url && (
                              <>
                                {' '}
                                •{' '}
                                <a
                                  href={siteContent.free_guide_pdf_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#5C0120] hover:underline"
                                  data-testid="admin-content-free-guide-current-file-link"
                                >
                                  Preview PDF
                                </a>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="space-y-4 pt-6 border-t border-stone-100">
                    <h4 className="font-medium">Footer Description</h4>
                    <Textarea
                      value={siteContent.footer_description || ''}
                      onChange={(e) => handleContentChange('footer_description', e.target.value)}
                      rows={2}
                      className="input-wedding resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="categories">
            <AdminCategoriesSection token={token} onCategoriesUpdated={loadVendorSelectableCategories} />
          </TabsContent>

          <TabsContent value="settings">
            <AdminSettingsSection token={token} />
          </TabsContent>

          <TabsContent value="account">
            <AdminAccountSection token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


// Admin Account Section - Password Change
function AdminAccountSection({ token }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-sm border border-stone-100 card-shadow">
      <div className="p-6 border-b border-stone-100">
        <div className="flex items-center gap-3">
          <Lock className="h-5 w-5 text-[#5C0120]" />
          <div>
            <h2 className="text-lg font-semibold">Account Settings</h2>
            <p className="text-sm text-stone-500">Manage your admin password</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-md">
        <h3 className="font-medium mb-4">Change Password</h3>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <Label htmlFor="admin-current-password">Current Password</Label>
            <Input
              id="admin-current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="mt-1"
              data-testid="admin-current-password-input"
            />
          </div>

          <div>
            <Label htmlFor="admin-new-password">New Password</Label>
            <Input
              id="admin-new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="mt-1"
              data-testid="admin-new-password-input"
            />
          </div>

          <div>
            <Label htmlFor="admin-confirm-password">Confirm New Password</Label>
            <Input
              id="admin-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-1"
              data-testid="admin-confirm-password-input"
            />
          </div>

          {message.text && (
            <div className={`p-3 rounded-sm text-sm ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <Button 
            type="submit" 
            disabled={loading}
            className="bg-[#5C0120] hover:bg-[#5C0120]/90"
            data-testid="admin-change-password-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Change Password
          </Button>
        </form>
      </div>
    </div>
  );
}


// Expo Content Section - Manage About Expo Page
function ExpoContentSection({ token }) {
  const [content, setContent] = useState({
    event_title: '',
    event_description: '',
    event_date: '',
    event_venue: '',
    hero_image: '',
    gallery_images: [],
    magazine_images: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/cms/expo-content`);
      setContent(response.data);
    } catch (err) {
      console.error('Failed to fetch expo content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API}/cms/expo-content`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Expo content saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save expo content' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Event Details */}
      <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6">
        <h3 className="font-medium text-lg mb-4">Event Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Event Title</Label>
            <Input
              value={content.event_title}
              onChange={(e) => handleChange('event_title', e.target.value)}
              placeholder="The Gibraltar Wedding Expo"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Event Date</Label>
            <Input
              value={content.event_date}
              onChange={(e) => handleChange('event_date', e.target.value)}
              placeholder="March 18th, 2024"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Event Venue</Label>
            <Input
              value={content.event_venue}
              onChange={(e) => handleChange('event_venue', e.target.value)}
              placeholder="Sunborn Gibraltar"
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Event Description</Label>
            <Textarea
              value={content.event_description}
              onChange={(e) => handleChange('event_description', e.target.value)}
              placeholder="Describe the expo event..."
              className="mt-1"
              rows={4}
            />
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6">
        <h3 className="font-medium text-lg mb-4">Hero Image (Memorable Moments Section)</h3>
        <p className="text-sm text-stone-500 mb-4">This image appears next to the event details text.</p>
        
        {content.hero_image && (
          <div className="mb-4 relative inline-block">
            <img 
              src={content.hero_image} 
              alt="Hero" 
              className="max-w-md h-auto rounded-sm border"
            />
            <button
              onClick={() => handleChange('hero_image', '')}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        <ImageUploader
          folder="expo"
          currentImage={content.hero_image}
          onUpload={(url) => handleChange('hero_image', url)}
        />
      </div>

      {/* Magazine Images */}
      <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6">
        <h3 className="font-medium text-lg mb-4">Globe Magazine Images</h3>
        <p className="text-sm text-stone-500 mb-4">
          Upload images of the magazine write-up. These will be displayed vertically (one below another).
        </p>
        
        {content.magazine_images && content.magazine_images.length > 0 && (
          <div className="space-y-4 mb-4">
            {content.magazine_images.map((img, index) => (
              <div key={index} className="relative inline-block w-full">
                <img 
                  src={img} 
                  alt={`Magazine ${index + 1}`} 
                  className="max-w-full h-auto rounded-sm border"
                />
                <button
                  onClick={() => {
                    const newImages = content.magazine_images.filter((_, i) => i !== index);
                    handleChange('magazine_images', newImages);
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <MultiImageUploader
          folder="expo/magazine"
          images={content.magazine_images || []}
          onUpload={(images) => handleChange('magazine_images', images)}
        />
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-4">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#5C0120] hover:bg-[#5C0120]/90"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Changes
        </Button>
        
        {message.text && (
          <span className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
}


// Admin Users Section - View registered couples and their feature usage
function AdminUsersSection({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [tempPasswords, setTempPasswords] = useState({});

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const getFeatureBadge = (count, label) => {
    if (count === 0) return null;
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#5C0120]/10 text-[#5C0120]">
        {count} {label}
      </span>
    );
  };

  const handleDeleteUser = async (user) => {
    const confirmMessage = user.is_admin
      ? `Delete admin account ${user.email}? This cannot be undone.`
      : `Delete user account ${user.email} and related planner data? This cannot be undone.`;

    if (!window.confirm(confirmMessage)) return;

    setDeletingUserId(user.id);
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleResetUserPassword = async (user) => {
    const temporaryPassword = (tempPasswords[user.id] || '').trim();
    if (temporaryPassword.length < 6) {
      toast.error('Temporary password must be at least 6 characters');
      return;
    }

    setResettingUserId(user.id);
    try {
      await axios.post(
        `${API}/admin/users/${user.id}/reset-password`,
        {
          temporary_password: temporaryPassword,
          force_change_on_login: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Temporary password set for ${user.email}`);
      setTempPasswords((prev) => ({ ...prev, [user.id]: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  // Calculate feature popularity
  const featureStats = {
    notes: users.reduce((sum, u) => sum + (u.feature_usage?.notes || 0), 0),
    guests: users.reduce((sum, u) => sum + (u.feature_usage?.guests || 0), 0),
    dates: users.reduce((sum, u) => sum + (u.feature_usage?.dates || 0), 0),
    budget_items: users.reduce((sum, u) => sum + (u.feature_usage?.budget_items || 0), 0),
    saved_vendors: users.reduce((sum, u) => sum + (u.feature_usage?.saved_vendors || 0), 0),
    messages: users.reduce((sum, u) => sum + (u.feature_usage?.messages || 0), 0),
    ai_chat: users.reduce((sum, u) => sum + (u.feature_usage?.ai_chat_sessions || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Feature Usage Overview */}
      <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6">
        <h3 className="font-medium text-lg mb-4">Feature Popularity</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.guests}</p>
            <p className="text-xs text-stone-500">Guests Added</p>
          </div>
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.notes}</p>
            <p className="text-xs text-stone-500">Notes Created</p>
          </div>
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.budget_items}</p>
            <p className="text-xs text-stone-500">Budget Items</p>
          </div>
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.dates}</p>
            <p className="text-xs text-stone-500">Dates Saved</p>
          </div>
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.saved_vendors}</p>
            <p className="text-xs text-stone-500">Vendors Saved</p>
          </div>
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.messages}</p>
            <p className="text-xs text-stone-500">Messages Sent</p>
          </div>
          <div className="text-center p-3 bg-stone-50 rounded">
            <p className="text-2xl font-bold text-[#5C0120]">{featureStats.ai_chat}</p>
            <p className="text-xs text-stone-500">AI Chat Sessions</p>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-sm border border-stone-100 card-shadow">
        <div className="p-4 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">User Accounts</h3>
              <p className="text-sm text-stone-500">{users.length} accounts across couples and admins</p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Registered</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Features Used</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Password Reset</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={`${user.is_current_admin ? 'bg-[#5C0120]/5' : 'hover:bg-stone-50'}`}
                  data-testid={`admin-user-row-${user.id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-stone-900">{user.name}</p>
                      {user.is_current_admin && (
                        <span className="rounded-full bg-[#5C0120] px-2 py-0.5 text-[11px] font-medium text-white" data-testid="admin-current-user-badge">
                          Current admin
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <a href={`mailto:${user.email}`} className="text-[#5C0120] hover:underline" data-testid={`admin-user-email-${user.id}`}>
                      {user.email}
                    </a>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${user.is_admin ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-700'}`} data-testid={`admin-user-role-${user.id}`}>
                      {user.is_admin ? 'Admin' : 'Couple'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-stone-500">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {getFeatureBadge(user.feature_usage?.notes, 'notes')}
                      {getFeatureBadge(user.feature_usage?.guests, 'guests')}
                      {getFeatureBadge(user.feature_usage?.dates, 'dates')}
                      {getFeatureBadge(user.feature_usage?.budget_items, 'budget')}
                      {getFeatureBadge(user.feature_usage?.saved_vendors, 'saved')}
                      {getFeatureBadge(user.feature_usage?.messages, 'msgs')}
                      {getFeatureBadge(user.feature_usage?.ai_chat_sessions, 'AI')}
                      {!user.feature_usage?.notes && !user.feature_usage?.guests && !user.feature_usage?.dates && 
                       !user.feature_usage?.budget_items && !user.feature_usage?.saved_vendors && 
                       !user.feature_usage?.messages && !user.feature_usage?.ai_chat_sessions && (
                        <span className="text-xs text-stone-400">No activity yet</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2" data-testid={`admin-user-reset-password-controls-${user.id}`}>
                      <Input
                        type="password"
                        placeholder="Temporary password"
                        value={tempPasswords[user.id] || ''}
                        onChange={(e) => setTempPasswords((prev) => ({ ...prev, [user.id]: e.target.value }))}
                        className="h-8 w-44"
                        disabled={user.is_admin || user.is_current_admin || resettingUserId === user.id}
                        data-testid={`admin-user-temp-password-input-${user.id}`}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleResetUserPassword(user)}
                        disabled={user.is_admin || user.is_current_admin || resettingUserId === user.id}
                        data-testid={`admin-reset-user-password-${user.id}`}
                        title={user.is_admin ? 'Use Account tab for admin password changes' : 'Set temporary password and force change on next login'}
                      >
                        {resettingUserId === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteUser(user)}
                      disabled={user.is_current_admin || deletingUserId === user.id}
                      className="text-red-600 hover:text-red-700 disabled:text-stone-300"
                      data-testid={`admin-delete-user-${user.id}`}
                      title={user.is_current_admin ? 'You cannot delete the admin account you are using now' : 'Delete account'}
                    >
                      {deletingUserId === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// Admin Settings Section - SEO and Utilities
function AdminSettingsSection({ token }) {
  const [slugGenerating, setSlugGenerating] = useState(false);
  const [slugMessage, setSlugMessage] = useState('');
  const previewEnvironment = isPreviewEnvironment();
  const [seoPreviewEnabled, setSeoPreviewEnabled] = useState(() => getSeoPreviewMode());

  const handleGenerateSlugs = async () => {
    setSlugGenerating(true);
    setSlugMessage('');
    try {
      const response = await axios.post(`${API}/admin/generate-vendor-slugs`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlugMessage(response.data.message);
    } catch (err) {
      setSlugMessage('Failed to generate slugs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSlugGenerating(false);
    }
  };

  const handleSeoPreviewToggle = (enabled) => {
    if (!previewEnvironment) return;
    setSeoPreviewEnabled(enabled);
    setSeoPreviewMode(enabled);
    toast.success(enabled ? 'SEO preview mode enabled in this browser' : 'SEO preview mode disabled in this browser');
  };

  const openPreviewModeVendorsPage = () => {
    if (typeof window === 'undefined') return;
    if (!previewEnvironment) return;
    window.location.href = '/vendors?seo-preview=1';
  };

  return (
    <div className="space-y-6">
      {/* SEO Settings */}
      <div className="bg-white rounded-sm border border-stone-100 card-shadow p-6">
        <h3 className="font-medium text-lg mb-2">SEO Settings</h3>
        <p className="text-sm text-stone-500 mb-6">Manage search engine optimization features.</p>
        
        <div className="border border-stone-100 rounded-sm p-4">
          <h4 className="font-medium mb-2">Vendor URL Slugs</h4>
          <p className="text-sm text-stone-500 mb-4">
            Generate SEO-friendly URLs for all vendors. This converts vendor URLs from 
            <code className="bg-stone-100 px-1 mx-1 text-xs">/vendors/abc123-uuid</code> to 
            <code className="bg-stone-100 px-1 mx-1 text-xs">/vendors/vendor-name</code>.
          </p>
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleGenerateSlugs} 
              disabled={slugGenerating}
              className="bg-[#5C0120] hover:bg-[#5C0120]/90"
            >
              {slugGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Generate Vendor Slugs
            </Button>
            {slugMessage && (
              <span className={`text-sm ${slugMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                {slugMessage}
              </span>
            )}
          </div>
        </div>

        <div className="border border-stone-100 rounded-sm p-4 mt-4" data-testid="admin-seo-preview-mode-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h4 className="font-medium mb-1">SEO Preview Mode</h4>
              <p className="text-sm text-stone-500">
                Show hidden/empty categories for SEO copy review in this browser only.
              </p>
              <p className="text-xs text-stone-400 mt-1" data-testid="admin-seo-preview-mode-note">
                {previewEnvironment
                  ? 'This does not change live category visibility for visitors.'
                  : 'Preview mode controls are disabled on live domains and only work in Emergent preview/local environments.'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-medium ${seoPreviewEnabled ? 'text-[#5C0120]' : 'text-stone-500'}`} data-testid="admin-seo-preview-mode-status">
                {seoPreviewEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={seoPreviewEnabled}
                onCheckedChange={handleSeoPreviewToggle}
                disabled={!previewEnvironment}
                data-testid="admin-seo-preview-mode-toggle"
              />
              <Button
                type="button"
                variant="outline"
                className="border-[#5C0120] text-[#5C0120] hover:bg-[#5C0120]/10"
                onClick={openPreviewModeVendorsPage}
                disabled={!previewEnvironment}
                data-testid="admin-open-seo-preview-link"
              >
                Open Preview
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Admin Categories Section - Manage and Reorder Categories
function AdminCategoriesSection({ token, onCategoriesUpdated }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryEmoji, setNewCategoryEmoji] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data) && response.data.length > 0) {
        setCategories(response.data);
      } else {
        // Use default from vendorCategories
        setCategories(vendorCategories.map((cat, index) => ({
          name: cat.name,
          slug: cat.slug,
          icon: cat.icon,
          description: cat.description,
          displayName: cat.displayName,
          sort_order: index
        })));
      }
    } catch (err) {
      // If no custom order exists, use default from vendorCategories
      setCategories(vendorCategories.map((cat, index) => ({
        name: cat.name,
        slug: cat.slug,
        icon: cat.icon,
        description: cat.description,
        displayName: cat.displayName,
        sort_order: index
      })));
    } finally {
      setLoading(false);
    }
  };

  const moveCategory = (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newCategories.length) return;
    
    // Swap categories
    [newCategories[index], newCategories[targetIndex]] = [newCategories[targetIndex], newCategories[index]];
    
    // Update sort_order
    newCategories.forEach((cat, idx) => {
      cat.sort_order = idx;
    });
    
    setCategories(newCategories);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(`${API}/admin/categories/order`, { categories }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Category order saved successfully!' });
      toast.success('Category order saved!');
      if (typeof onCategoriesUpdated === 'function') {
        onCategoriesUpdated();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save category order' });
      toast.error('Failed to save category order');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    // Generate slug from name
    const slug = 'gibraltar-' + newCategoryName.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s-]+/g, '-').trim();
    
    const newCategory = {
      name: newCategoryName.trim(),
      slug: slug,
      icon: newCategoryEmoji || '📋',
      description: `${newCategoryName.trim()} services for your Gibraltar wedding`,
      displayName: `Gibraltar ${newCategoryName.trim()}`,
      sort_order: categories.length,
      visible: true
    };
    
    setCategories([...categories, newCategory]);
    setNewCategoryName('');
    setNewCategoryEmoji('');
    setShowAddForm(false);
    toast.success('Category added! Remember to save the order.');
  };

  const handleDeleteCategory = (index) => {
    if (!window.confirm(`Delete category "${categories[index].name}"? This will NOT delete vendors in this category.`)) {
      return;
    }
    const newCategories = categories.filter((_, idx) => idx !== index);
    newCategories.forEach((cat, idx) => {
      cat.sort_order = idx;
    });
    setCategories(newCategories);
    toast.success('Category removed! Remember to save changes.');
  };

  const toggleVisibility = (index) => {
    const newCategories = [...categories];
    newCategories[index].visible = newCategories[index].visible === false ? true : false;
    setCategories(newCategories);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#5C0120]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-sm border border-stone-100 card-shadow">
        <div className="p-6 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-[#5C0120]" />
              <div>
                <h2 className="text-lg font-semibold">Manage Categories</h2>
                <p className="text-sm text-stone-500">Reorder categories and add new ones with emojis</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAddForm(!showAddForm)}
                data-testid="admin-add-category-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
              <Button
                onClick={handleSaveOrder}
                disabled={saving}
                className="bg-[#5C0120] hover:bg-[#5C0120]/90"
                data-testid="admin-save-categories-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Order
              </Button>
            </div>
          </div>
        </div>

        {/* Add Category Form */}
        {showAddForm && (
          <div className="p-4 bg-stone-50 border-b border-stone-100">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Label>Category Name</Label>
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Wedding Transport"
                  className="mt-1"
                  data-testid="admin-new-category-name"
                />
              </div>
              <div className="w-32">
                <Label>Emoji Icon</Label>
                <Input
                  value={newCategoryEmoji}
                  onChange={(e) => setNewCategoryEmoji(e.target.value)}
                  placeholder="🚗"
                  className="mt-1 text-center text-xl"
                  maxLength={4}
                  data-testid="admin-new-category-emoji"
                />
              </div>
              <Button onClick={handleAddCategory} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90">
                Add
              </Button>
              <Button variant="ghost" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className="divide-y divide-stone-100">
          {categories.map((category, index) => (
            <div 
              key={category.slug || index}
              className={`flex items-center gap-4 p-4 hover:bg-stone-50 transition-colors ${category.visible === false ? 'opacity-50' : ''}`}
              data-testid={`admin-category-row-${index}`}
            >
              {/* Drag Handle (visual indicator) */}
              <div className="text-stone-300">
                <GripVertical className="h-5 w-5" />
              </div>
              
              {/* Order Position */}
              <div className="w-8 h-8 bg-[#5C0120]/10 rounded-full flex items-center justify-center text-sm font-medium text-[#5C0120]">
                {index + 1}
              </div>
              
              {/* Emoji/Icon */}
              <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center text-2xl">
                {category.icon === 'gib-flag' ? (
                  <img src="https://flagcdn.com/w40/gi.png" alt="Gibraltar" className="h-6 w-auto" />
                ) : (
                  category.icon
                )}
              </div>
              
              {/* Category Info */}
              <div className="flex-1">
                <p className={`font-medium ${category.visible === false ? 'text-stone-400' : 'text-stone-900'}`}>
                  {category.name}
                  {category.visible === false && <span className="ml-2 text-xs text-stone-400">(Hidden)</span>}
                </p>
                <p className="text-xs text-stone-500">/{category.slug}</p>
              </div>
              
              {/* Visibility Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toggleVisibility(index)}
                className={`h-8 w-8 ${category.visible === false ? 'text-stone-400' : 'text-green-600'}`}
                title={category.visible === false ? 'Show category' : 'Hide category'}
                data-testid={`admin-category-visibility-${index}`}
              >
                {category.visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              
              {/* Move Buttons */}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveCategory(index, 'up')}
                  disabled={index === 0}
                  className="h-8 w-8"
                  data-testid={`admin-category-up-${index}`}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => moveCategory(index, 'down')}
                  disabled={index === categories.length - 1}
                  className="h-8 w-8"
                  data-testid={`admin-category-down-${index}`}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteCategory(index)}
                  className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid={`admin-category-delete-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {message.text}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
        <h4 className="font-medium text-amber-800 mb-2">How Category Management Works</h4>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• Use the <strong>up/down arrows</strong> to reorder categories</li>
          <li>• Click the <strong>eye icon</strong> to show/hide categories on the website</li>
          <li>• Hidden categories will not appear publicly unless SEO Preview Mode is enabled in your browser</li>
          <li>• Categories will display in this order across the site</li>
          <li>• Click <strong>&quot;Save Order&quot;</strong> to persist your changes</li>
        </ul>
      </div>
    </div>
  );
}
